import { lazy, Suspense, useContext } from 'react';

import {
  Box,
  alpha,
  Stack,
  IconButton,
  Tooltip,
  styled,
  useTheme
} from '@mui/material';
import MenuTwoToneIcon from '@mui/icons-material/MenuTwoTone';
import { SidebarContext } from 'src/contexts/SidebarContext';
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';

import HeaderButtons from './Buttons';
import HeaderUserbox from './Userbox';

// Loaded lazily: the header mounts at app boot, and importing MUI's Autocomplete/Popper that early
// evaluates them before the styling engine is initialised in Vite's pre-bundled dev chunks
// ("styled_default is not a function"). It also keeps the search out of the main bundle.
const GlobalSearch = lazy(() => import('./Buttons/Search/GlobalSearch'));

const HeaderWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'sidebarOpen'
})<{ sidebarOpen: boolean }>(
  ({ theme, sidebarOpen }) => `
        height: ${theme.header.height};
        color: ${theme.header.textColor};
        padding: ${theme.spacing(0, 2)};
        right: 0;
        z-index: 6;
        background-color: ${alpha(theme.header.background, 0.95)};
        backdrop-filter: blur(3px);
        position: fixed;
        justify-content: space-between;
        width: 100%;
        @media (min-width: ${theme.breakpoints.values.lg}px) {
            left: ${sidebarOpen ? theme.sidebar.width : 0};
            width: auto;
        }
`
);

function Header() {
  const { sidebarToggle, toggleSidebar, toggleSidebarDesktop, isSidebarOpen } = useContext(SidebarContext);
  const theme = useTheme();

  return (
    <HeaderWrapper
      sidebarOpen={isSidebarOpen}
      display="flex"
      alignItems="center"
      sx={{
        // Compact bar: flat surface with a hairline instead of the old heavy shadow
        backgroundColor: theme.palette.background.paper,
        boxShadow: 'none',
        borderBottom: `1px solid ${alpha(theme.palette.common.black, 0.08)}`
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0, mr: 2 }}>
        <Box sx={{ display: { xs: 'none', lg: 'inline-block' } }}>
          <Tooltip arrow title="Toggle Menu">
            <IconButton color="primary" onClick={toggleSidebarDesktop}>
              <MenuTwoToneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Suspense fallback={<Box sx={{ width: { sm: 280, md: 360, lg: 440 }, height: 40 }} />}>
          <GlobalSearch />
        </Suspense>
      </Stack>
      <Box display="flex" alignItems="center">
        <HeaderButtons />
        <HeaderUserbox />
        <Box
          component="span"
          sx={{
            ml: 2,
            display: { lg: 'none', xs: 'inline-block' }
          }}
        >
          <Tooltip arrow title="Toggle Menu">
            <IconButton color="primary" onClick={toggleSidebar}>
              {!sidebarToggle ? (
                <MenuTwoToneIcon fontSize="small" />
              ) : (
                <CloseTwoToneIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </HeaderWrapper>
  );
}

export default Header;
